// The React component registry is a separate chunk, fetched only by a page the
// server rendered a component on — see react_mount.js for why, and for the
// mount it has to do itself once it arrives after DOMContentLoaded.
if (document.querySelector("[data-react-class]")) {
  import(/* webpackChunkName: "react-components" */ "../react_mount");
}

// cocoon.js closes with `})(jQuery)` — it reads the GLOBAL when the module is
// evaluated, and require("jquery") does not create one. jQuery's UMD passes
// noGlobal whenever module.exports exists, so under webpack it hands back the
// module and never assigns window.jQuery.
//
// Every page that worked did so only because auth_user emitted
// javascript_include_tag "jquery" — a blocking tag below the pack tag, which
// won only because a classic blocking script runs before any deferred one.
// Since 2026-09-22 that tag is deferred too (so it stops blocking the parser)
// and sits ABOVE the pack tag, and deferred scripts run in document order: it
// still runs first, but now because of source order. Neither arrangement was
// ever safe to rely on from here, which is why this line exists.
//
// mock_room ships no jQuery at all — deliberately; it is the stripped layout
// for the exam room, the result and the review workbench — so there the pack
// threw "jQuery is not defined" and stopped on this line. React had already
// mounted by then, which is why those pages looked fine. What never ran was
// everything below: chartkick, subject_autocomplete and the pack-level
// setupAxiosAuthHandling(). That last one was survivable only by luck — both
// React surfaces in that layout call setupAxiosCSRF(), which registers the
// interceptor itself — and it is not a guarantee anything added below this
// line inherits.
//
// Assigned with `||` rather than outright: a layout that DOES ship the
// Sprockets jQuery has inline scripts and plugins bound to that instance, and
// replacing it here would leave them attached to an object nothing else uses.
const bundledJQuery = require("jquery");
window.jQuery = window.jQuery || bundledJQuery;
window.$ = window.$ || bundledJQuery;

require("@nathanvda/cocoon");
// Chartkick + chart.js (~200KB) only where a chart is drawn — two admin pages.
// The gem's server-rendered <script> for each chart creates it at once if
// window.Chartkick exists and otherwise waits for the "chartkick:load" event
// chartkick.js dispatches when it loads (chartkick-5.2.1 helper.rb), so a late
// load draws the same chart. That listener is also the marker this looks for.
if (Array.from(document.scripts).some((s) => s.text.includes("chartkick:load"))) {
  import(/* webpackChunkName: "chartkick" */ "chartkick/chart.js");
}
import "./subject_autocomplete";
import { setupAxiosAuthHandling } from "../helpers";
// The course finder: the search combobox and the picker's cascade. A plain
// module rather than a pack, because anything under packs/ becomes its own
// webpack entrypoint that no layout loads.
import "../course_finder";
// After course_finder: it opens the searches that module binds.
import "../finder_nudge";
// The admin compose form: the drawable counts follow the banks that are ticked.
// Same reasoning as the line above — a plain module, not a pack.
import "../mock_composer";
import "../mock_request_wizard";
import "../mock_request_sections";
// The admin campaign form: scope lists and the kind toggle.
import "../promotion_form";
import "../assessments_modal";
import "../course_report_poll";
import "../class_notes_poll";
import "../class_notes_reader";
// The report's "Unpack this question" card: start, poll, reveal.
import "../question_elaboration";
import "../integrity_explanation";
// "Think our answer is wrong?": the two-reviewer check, polled and revealed.
import "../answer_key_contest";
// The copy guard on every page in layouts/mock_room: keyed on the body
// attribute that layout sets, so it is inert everywhere else.
import "../protected_content";
// The sidebar's collapse button: keyed on [data-sidebar-toggle], which only
// layouts/auth_user renders, so it is inert everywhere else.
import "../sidebar_toggle";
// The theme switch: keyed on form[data-theme-choice], which only the chrome
// renders, so it is inert in layouts/mock_room and everywhere else.
import "../theme_toggle";
// The course tabs bring the current tab into view on a phone.
import "../course_tabs";
// The study-note reader: keyed on article.study-note[data-progress-url],
// which only study_notes/show renders, so it is inert everywhere else.
import "../study_note_reader";
import "../chosen_paper_picker";
// The free trial's picker: keyed on [data-trial-picker], course_trials/new only.
import "../course_trial_picker";
import "../study_calendar";
// The eye on a password field: keyed on [data-password-toggle], which only
// password_field_with_toggle renders, so it is inert everywhere else.
import "../password_toggle";
// The gift share page: copy the link, or hand it to the phone's share sheet.
import "../gift_share";
// A team's share card: reports each tap as team_shared. Keyed on
// [data-team-share], which only course_teams/_share_card renders.
import "../team_share";
// The team room: caps the week plan's ticks at the limit its form declares.
import "../team_room";
// The home page's example cards: keyed on .home-scene, which only
// pages/welcome/*_scene render, so it is inert everywhere else. The still
// card is the default; this only adds the scroll-in.
import "../home_reveal";

// Register once for every page, so a component that posts without calling
// setupAxiosCSRF still recovers from an expired session.
setupAxiosAuthHandling();

// KaTeX, for the maths in a mock question.
//
// The stylesheet is imported HERE rather than into app/assets/stylesheets
// because it is a bundled library's own CSS and travels with the library —
// Sprockets cannot reach node_modules, and vendoring a copy would drift from
// the pinned version. It is what gives the `application` entrypoint a CSS
// asset, which `stylesheet_pack_tag` on mock_room and auth_user then links.
//
// THOSE TWO CHANGES ARE ONE CHANGE. `stylesheet_pack_tag` calls the manifest's
// BANG lookup, so on an entrypoint with no CSS it does not degrade — it raises
// MissingEntryError and takes the page down. Removing this import without
// removing the tags 500s the exam room. (And `hmr: false` in shakapacker.yml
// means mini-css-extract is used in EVERY environment here, so there is no
// development mode in which the tag is a no-op and this goes unnoticed.)
import "katex/dist/katex.min.css";
import typesetMath from "../math_typeset";

// KaTeX's JS is NOT in this entrypoint. It used to be, through the eager
// component context (MathText imports renderTex, which imports katex), which
// made scoping it here pointless. With the registry lazy (react_mount.js),
// KaTeX rides in two late chunks instead: the component registry, for the
// React surfaces, and math_typeset.js, for the server-rendered maths below.

// The report and the admin item pages are server-rendered, so their maths
// arrives as `<span class="mq-tex" data-tex="…">` and is typeset here. The
// React surfaces do their own and are unaffected by this line.
//
// javascript_pack_tag is deferred (Shakapacker 7 defaults defer: true), so
// DOMContentLoaded may have fired already by the time this runs.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => typesetMath());
} else {
  typesetMath();
}
